import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/Reveal";

export default function About() {
  return (
    <div data-testid="about-page" className="pt-20">
      <header className="max-w-[1400px] mx-auto px-6 md:px-10 pt-20 md:pt-28 pb-16 md:pb-24">
        <Reveal><Eyebrow className="mb-6">Est. United Kingdom — About</Eyebrow></Reveal>
        <Reveal delay={0.1}>
          <h1 className="font-serif text-5xl md:text-7xl tracking-tight max-w-4xl" data-testid="about-headline">
            A legacy of <span className="italic text-green">skilled hands</span> and honest work.
          </h1>
        </Reveal>
      </header>

      {/* CHAPTER 01 */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 pb-28 md:pb-40 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-3">
          <Reveal>
            <p className="font-serif text-7xl text-line select-none">01</p>
            <Eyebrow className="mt-4">Chapter One</Eyebrow>
          </Reveal>
        </div>
        <div className="lg:col-span-9 grid md:grid-cols-2 gap-12 items-start">
          <div>
            <Reveal delay={0.1}>
              <h2 className="font-serif text-4xl md:text-5xl tracking-tight" data-testid="hey-there-headline">
                Hey <span className="italic">There</span>
              </h2>
            </Reveal>
            <Reveal delay={0.2} className="mt-8 space-y-6">
              <p className="text-smoke leading-relaxed">
                We're a UK-based hatmaker crafting premium tweed and wax cotton
                hats in the British tradition — where style and durability have
                always kept company.
              </p>
              <p className="text-smoke leading-relaxed">
                Each hat, from Blackwatch to Honey tartan, tells a story of
                heritage: of mills in the Borders, of weather and work, of a
                way of making things that refuses to hurry.
              </p>
            </Reveal>
          </div>
          <Reveal delay={0.25}>
            <img
              src="/images/tartan-texture.png"
              alt="Honey tartan weave in detail"
              data-testid="about-texture-image"
              className="w-full aspect-[4/5] object-cover border border-line"
            />
            <p className="eyebrow !text-[0.6rem] text-smoke mt-4">Cloth before everything</p>
          </Reveal>
        </div>
      </section>

      {/* INTERLUDE IMAGE */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 pb-28 md:pb-40">
        <Reveal>
          <div className="relative overflow-hidden border border-line">
            <img
              src="/images/factory.jpg"
              alt="Inside the Capstone sewing factory"
              data-testid="about-factory-image"
              className="w-full aspect-[21/9] object-cover"
            />
            <div className="absolute bottom-0 left-0 bg-ivory border-t border-r border-line px-8 py-5">
              <p className="eyebrow !text-[0.6rem] text-honey">The factory floor — United Kingdom</p>
            </div>
          </div>
        </Reveal>
      </section>

      {/* FACTORY STRIP */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 pb-28 md:pb-40" data-testid="factory-strip">
        <Reveal>
          <div className="flex items-end justify-between mb-10">
            <Eyebrow>Inside the factory</Eyebrow>
            <p className="eyebrow !text-[0.6rem] text-smoke hidden md:block">
              Cutting · Machining · Finishing
            </p>
          </div>
        </Reveal>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
          {[
            { src: "/images/factory-hands.jpg", label: "The machining", alt: "Sewing work at the factory" },
            { src: "/images/factory-cloth.jpg", label: "The mood", alt: "Tartan in the wild" },
            { src: "/images/factory-detail.jpg", label: "The detail", alt: "Tweed weave up close" },
            { src: "/images/factory-output.jpg", label: "The wearing", alt: "A Capstone hat, worn well" },
          ].map((img, i) => (
            <Reveal key={img.src} delay={i * 0.08}>
              <div className="overflow-hidden border border-line group">
                <img
                  src={img.src}
                  alt={img.alt}
                  loading="lazy"
                  data-testid={`factory-strip-image-${i + 1}`}
                  className="w-full aspect-[3/4] object-cover group-hover:scale-[1.04] transition-transform duration-700 ease-out"
                />
              </div>
              <p className="eyebrow !text-[0.6rem] text-smoke mt-3">{img.label}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CHAPTER 02 */}
      <section className="bg-cream border-y border-line">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-28 md:py-40 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-3">
            <Reveal>
              <p className="font-serif text-7xl text-line select-none">02</p>
              <Eyebrow className="mt-4">Chapter Two</Eyebrow>
            </Reveal>
          </div>
          <div className="lg:col-span-6">
            <Reveal delay={0.1}>
              <h2 className="font-serif text-4xl md:text-5xl tracking-tight" data-testid="all-about-me-headline">
                All About <span className="italic">Me</span>
              </h2>
            </Reveal>
            <Reveal delay={0.2} className="mt-8 space-y-6">
              <p className="text-smoke leading-relaxed">
                Capstone began with a simple conviction: that British
                manufacturing is worth reviving — one hat at a time.
              </p>
              <p className="text-smoke leading-relaxed">
                I started this company to bring back the pride of honest work:
                traditional methods, timeless materials, and the quiet
                satisfaction of a thing made properly. Every cap is cut,
                sewn and finished by hand, the way it has been done for
                generations.
              </p>
              <p className="text-smoke leading-relaxed">
                Each hat carries a legacy of skilled hands — the cutters,
                machinists and finishers who came before us, and those we're
                teaching now. When you wear a Capstone, you carry that legacy
                too.
              </p>
            </Reveal>
            <Reveal delay={0.3} className="mt-12">
              <p className="font-serif italic text-2xl text-green" data-testid="founder-signature">
                — The Founder, Capstone Hats LTD
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-3 flex flex-col justify-end gap-8">
            <Reveal delay={0.25} className="w-full">
              <img
                src="/images/founder-factory.png"
                alt="The founder of Capstone Hats reviewing fabric sample books on the factory floor"
                data-testid="about-founder-image"
                className="w-full aspect-[3/4] object-cover object-top border border-line"
              />
              <p className="eyebrow !text-[0.6rem] text-smoke mt-4">The founder — reviewing materials on the factory floor</p>
            </Reveal>
            <Reveal delay={0.3} className="w-full border border-line bg-ivory p-8">
              <p className="eyebrow !text-[0.6rem] text-honey mb-4">The promise</p>
              <p className="font-serif text-xl leading-relaxed">
                "If it doesn't meet the standard, it doesn't leave the factory."
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* CHAPTER 03 — COMING SOON */}
      <section className="bg-green text-ivory" data-testid="coming-soon-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-28 md:py-40 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-3">
            <Reveal>
              <p className="font-serif text-7xl text-ivory/15 select-none">03</p>
              <Eyebrow className="!text-honey mt-4">Chapter Three</Eyebrow>
            </Reveal>
          </div>
          <div className="lg:col-span-9">
            <Reveal delay={0.1}>
              <Eyebrow className="!text-honey mb-6">Coming soon</Eyebrow>
              <h2 className="font-serif text-4xl md:text-6xl tracking-tight leading-[1.1]" data-testid="coming-soon-headline">
                Our own <span className="italic">in-house brand.</span>
              </h2>
            </Reveal>
            <Reveal delay={0.2} className="mt-10 max-w-2xl space-y-6">
              <p className="text-ivory/75 leading-relaxed">
                Soon, Capstone becomes more than hats. We're developing our own
                clothing brand — designed, cut and sewn right here in our UK
                factory. Outerwear, shirting, and much more, made with the same
                honest standards and the same skilled hands.
              </p>
              <p className="text-ivory/75 leading-relaxed">
                Everything under one roof, everything made properly. We can't
                wait to show you.
              </p>
              <p className="font-serif italic text-2xl text-honey" data-testid="coming-soon-tagline">
                Watch this space.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-28 md:py-36 text-left grid md:grid-cols-12 gap-8 items-center">
        <div className="md:col-span-8">
          <Reveal>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight">
              See what the hands have made.
            </h2>
          </Reveal>
        </div>
        <div className="md:col-span-4 md:text-right">
          <Reveal delay={0.15}>
            <Link
              to="/gallery"
              data-testid="about-gallery-cta"
              className="group inline-flex items-center gap-3 bg-green text-ivory eyebrow !text-[0.625rem] px-8 py-5 hover:bg-ink transition-colors duration-300"
            >
              Visit the gallery
              <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform duration-300" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
